import { useEffect, useState } from "react";

const sections = [
  { id: "introduction", label: "0.1 Introduction" },
  { id: "about", label: "1. About PrecisionMatch" },
  { id: "eligibility", label: "2. Eligibility" },
  { id: "user-accounts", label: "3. User accounts" },
  { id: "use-of-platform", label: "4. Use of the platform" },
  { id: "candidate-data", label: "5. Candidate and hiring data" },
  { id: "ai-disclaimer", label: "6. AI and hiring insights disclaimer" },
  { id: "subscriptions", label: "7. Subscriptions and payments" },
  { id: "intellectual-property", label: "8. Intellectual property" },
  { id: "data-security", label: "9. Data security" },
  { id: "service-availability", label: "10. Service availability" },
  { id: "termination", label: "11. Termination" },
  { id: "changes", label: "12. Changes to these terms" },
  { id: "governing-law", label: "13. Governing Law" },
  { id: "contact", label: "14. Contact Information" },
];

const TermsOfUse = () => {
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(({ id }) => ({
        id,
        el: document.getElementById(id),
      }));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const { id, el } = sectionElements[i];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 240) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 200;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="lg:w-[85%] w-[90%] mx-auto py-16 sm:py-24 mt-20">
      <h1 className="text-4xl sm:text-6xl font-semibold text-[#00003F] mb-12">
        Terms of Service
      </h1>

      <div className="flex flex-col lg:flex-row gap-12 lg:gap-36">
        {/* Sidebar Navigation */}
        <nav className="shrink-0">
          <div className="lg:sticky lg:top-[120px]">
            <ul className="space-y-5 pl-4 lg:pt-[30px]">
              {sections.map(({ id, label }) => (
                <li key={id}>
                  <button
                    onClick={() => scrollToSection(id)}
                    className={`text-sm sm:text-lg lg:text-[22px] text-left transition-colors ${
                      activeSection === id
                        ? "text-[#000B55] font-medium"
                        : "text-[#667085] hover:text-[#000B55]"
                    }`}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 text-sm sm:text-lg lg:text-[22px]">
          {/* Introduction */}
          <section id="introduction" className="mb-12">
            <p className="text-sm sm:text-lg lg:text-[22px] text-[#667085] mb-2">
              Last Updated: 11th March, 2026.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-1">
              These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the
              PrecisionMatch platform, website, and services (collectively, the
              &ldquo;Service&rdquo;). By accessing or using PrecisionMatch, you agree to be
              bound by these Terms.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              If you do not agree with these Terms, you may not use the Service.
            </p>
          </section>

          {/* 1. About PrecisionMatch */}
          <section id="about" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              1. About PrecisionMatch
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              PrecisionMatch provides software designed to help organizations
              structure and improve their hiring processes. The platform offers
              tools for job design, CV analysis, candidate comparison, and hiring
              insights.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              PrecisionMatch provides decision-support tools only and does not
              make hiring decisions on behalf of users unless asked to.
            </p>
          </section>

          {/* 2. Eligibility */}
          <section id="eligibility" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              2. Eligibility
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              To use PrecisionMatch, you must:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Be at least 18 years old</li>
              <li>
                Own a business/company or have the authority to act on behalf of
                your company or organization (if registering on behalf of one)
              </li>
              <li>Provide accurate account information</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              You are responsible for maintaining the confidentiality of your
              account credentials.
            </p>
          </section>

          {/* 3. User accounts */}
          <section id="user-accounts" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              3. User accounts
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              To access certain features, you must create an account.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              You agree to:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>
                Provide accurate and complete information during registration
              </li>
              <li>Keep your login credentials secure</li>
              <li>Notify us immediately of unauthorized account use</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              PrecisionMatch is not responsible for losses resulting from
              unauthorized access to your account.
            </p>
          </section>

          {/* 4. Use of the platform */}
          <section id="use-of-platform" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              4. Use of the platform
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              You agree to use PrecisionMatch only for lawful purposes and in
              accordance with these Terms.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              You may not:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Use the platform for illegal or fraudulent activities</li>
              <li>Upload harmful, offensive, or unlawful content</li>
              <li>Attempt to reverse engineer or copy the platform</li>
              <li>Interfere with or disrupt the service</li>
              <li>
                Attempt to access systems or data without authorization
              </li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              We reserve the right to suspend or terminate accounts that violate
              these rules.
            </p>
          </section>

          {/* 5. Candidate and hiring data */}
          <section id="candidate-data" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              5. Candidate and hiring data
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              PrecisionMatch allows users to upload and analyze candidate
              information, including CVs and employment history.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              By using the platform, you confirm that:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>
                You have the legal right to upload and process candidate data
              </li>
              <li>
                You comply with all applicable employment and data protection
                laws
              </li>
              <li>
                You are responsible for how candidate information is collected
                and used
              </li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              PrecisionMatch processes this data solely to provide the
              platform&apos;s hiring evaluation features.
            </p>
          </section>

          {/* 6. AI and hiring insights disclaimer */}
          <section id="ai-disclaimer" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              6. AI and hiring insights disclaimer
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              PrecisionMatch uses artificial intelligence to assist with tasks
              such as:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Job description generation</li>
              <li>CV analysis and comparison</li>
              <li>Candidate evaluation insights</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              These outputs are informational and advisory only.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              PrecisionMatch does not guarantee hiring outcomes, and all hiring
              decisions remain the sole responsibility of the user.
            </p>
          </section>

          {/* 7. Subscriptions and payments */}
          <section id="subscriptions" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              7. Subscriptions and payments
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              Certain features of PrecisionMatch may require a paid
              subscription.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              By subscribing, you agree that:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>
                Fees will be charged according to the pricing plan you select
              </li>
              <li>
                Payments may be billed on a recurring basis (monthly or
                annually)
              </li>
              <li>
                Failure to pay may result in suspension of your account
              </li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              Pricing and subscription terms may change in the future with notice
              to users.
            </p>
          </section>

          {/* 8. Intellectual property */}
          <section id="intellectual-property" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              8. Intellectual property
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              All rights, title, and interest in the PrecisionMatch platform,
              including software, algorithms, branding, and content, belong to
              PrecisionMatch or its licensors.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              Users may not:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Copy or reproduce the platform</li>
              <li>Distribute or resell the software</li>
              <li>
                Use PrecisionMatch intellectual property without permission
              </li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              You retain ownership of any data you upload to the platform.
            </p>
          </section>

          {/* 9. Data security */}
          <section id="data-security" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              9. Data security
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              PrecisionMatch takes reasonable technical and organizational
              measures to protect user data.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              However, no digital system can guarantee complete security, and
              users acknowledge that they use the platform at their own risk.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              For more information about how we handle data, please refer to our
              Privacy Policy.
            </p>
          </section>

          {/* 10. Service availability */}
          <section id="service-availability" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              10. Service availability
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              We aim to provide a reliable service but do not guarantee that
              PrecisionMatch will always be available without interruption.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-3">We may:</p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Modify features</li>
              <li>Perform maintenance</li>
              <li>Temporarily suspend the platform when necessary</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              We are not liable for temporary service disruptions.
            </p>
          </section>

          {/* 11. Termination */}
          <section id="termination" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              11. Termination
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              We may suspend or terminate your access to PrecisionMatch if:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>You violate these Terms</li>
              <li>Your account remains unpaid</li>
              <li>
                We believe your use poses a risk to the platform or other users
              </li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              Users may also stop using the platform at any time.
            </p>
          </section>

          {/* 12. Changes to these terms */}
          <section id="changes" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              12. Changes to these terms
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              We may update these Terms periodically.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              When significant changes occur, we will notify users through the
              platform or via email. Continued use of PrecisionMatch after
              updates constitutes acceptance of the revised Terms.
            </p>
          </section>

          {/* 13. Governing Law */}
          <section id="governing-law" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              13. Governing Law
            </h2>
            <p className="text-[#1D2939] leading-relaxed">
              These Terms shall be governed by and interpreted in accordance with
              the laws of Nigeria.
            </p>
          </section>

          {/* 14. Contact Information */}
          <section id="contact" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              14. Contact Information
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <p className="text-[#1D2939] leading-relaxed">PrecisionMatch</p>
            <p className="text-[#1D2939] leading-relaxed">
              Email: contact@precisionmatch.ai
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              Website: www.precisionmatch.ai
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export { TermsOfUse };
